import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PrioCollapse from '@prio365/prio365-react-library/lib/Collapse';
import { PrioItemType } from '@prio365/prio365-react-library/lib/Collapse/components/Collapse';
import {
  PromanReport,
  PromanReportCollection,
  PromanDocument,
  PromanFolder,
} from '../../../../../api/promanDtos';
import { PromanReportList } from './PromanReportList';
import {
  collectionHasMatchingReports,
  folderHasMatchingReports,
} from '../utils/promanHelpers';
import PrioSpinner from '@prio365/prio365-react-library/lib/Spinner/components/PrioSpinner';
import Empty from '@prio365/prio365-react-library/lib/Empty/components/Empty';
import Input from '@prio365/prio365-react-library/lib/Input/components/Input';

interface PromanSidebarProps {
  // Data
  folderGroups: Record<number, PromanReportCollection[]>;
  folders: PromanFolder[];
  reportsByCollection: Record<number, PromanReport[]>;
  organizedDocuments: Record<string, PromanDocument[]>;
  loading: boolean;
  loadingReports: Record<number, boolean>;
  loadingAllReports: boolean;
  reloadKey: number;

  // Navigation state
  activeFolders: (string | number)[];
  activeCollections: (string | number)[];
  activeReports: (string | number)[];
  selectedReportId: number | null;
  searchQuery: string;

  // Handlers
  onFolderChange: (keys: (string | number) | (string | number)[]) => void;
  onCollectionChange: (keys: (string | number) | (string | number)[]) => void;
  onReportChange: (keys: (string | number) | (string | number)[]) => void;
  onReportClick: (report: PromanReport) => void;
  onSearchChange: (query: string) => void;

  // Styles
  classes: {
    searchBar: string;
    collapseFolderPanel: string;
    nestedCollapse: string;
    collectionHeader: string;
    loadingContainer: string;
    emptyState: string;
    reportsList: string;
    reportItem: string;
    reportItemContent: string;
    reportItemHeader: string;
    reportCode: string;
    reportDate: string;
    reportPlace: string;
    reportStatus: string;
  };
}

export const PromanSidebar: React.FC<PromanSidebarProps> = ({
  folderGroups,
  folders,
  reportsByCollection,
  organizedDocuments,
  loading,
  loadingReports,
  loadingAllReports,
  reloadKey,
  activeFolders,
  activeCollections,
  selectedReportId,
  searchQuery,
  onFolderChange,
  onCollectionChange,
  onReportClick,
  onSearchChange,
  classes,
}) => {
  const { t } = useTranslation();

  // Generate collection items for a folder
  const getCollectionItems = useCallback(
    (collections: PromanReportCollection[]): PrioItemType[] => {
      // Filter collections to only show those with matching reports
      const filteredCollections = collections.filter((collection) =>
        collectionHasMatchingReports(
          collection.reportCollectionId || 0,
          reportsByCollection,
          searchQuery,
          loadingAllReports
        )
      );

      return filteredCollections.map((collection) => ({
        key: collection.reportCollectionId,
        headerLabel: `${
          collection.collectionLong === ''
            ? collection.collectionShort
            : collection.collectionLong
        }`,
        headerClass: classes.collectionHeader,
        children: (
          <div key={`${collection.reportCollectionId}_${reloadKey}`}>
            <PromanReportList
              collectionId={collection.reportCollectionId}
              reports={reportsByCollection[collection.reportCollectionId] || []}
              isLoading={loadingReports[collection.reportCollectionId] || false}
              searchQuery={searchQuery}
              organizedDocuments={organizedDocuments}
              selectedReportId={selectedReportId}
              onReportClick={onReportClick}
              classes={classes}
            />
          </div>
        ),
      }));
    },
    [
      reportsByCollection,
      loadingReports,
      searchQuery,
      loadingAllReports,
      organizedDocuments,
      selectedReportId,
      onReportClick,
      classes,
      reloadKey,
    ]
  );

  // Generate folder collapse items
  const folderCollapseItems: PrioItemType[] = useMemo(() => {
    // Filter folders to only show those with collections that have matching reports
    const filteredFolderGroups = Object.entries(folderGroups).filter(
      ([, collections]) =>
        folderHasMatchingReports(
          collections,
          reportsByCollection,
          searchQuery,
          loadingAllReports
        )
    );

    return filteredFolderGroups.map(([folderId, collections]) => ({
      key: `folder-${folderId}`,
      headerLabel: `${folders?.find((folder) => folder.folderID === Number(folderId))?.folderShort || folderId}`,
      children: (
        <PrioCollapse
          items={getCollectionItems(collections)}
          activeKey={activeCollections}
          onChange={onCollectionChange}
          className={classes.nestedCollapse}
        />
      ),
    }));
  }, [
    folderGroups,
    reportsByCollection,
    searchQuery,
    loadingAllReports,
    activeCollections,
    classes.nestedCollapse,
    onCollectionChange,
    getCollectionItems,
    folders,
  ]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <PrioSpinner size="small" />
      </div>
    );
  }

  if (Object.keys(folderGroups).length === 0) {
    return (
      <div className={classes.emptyState}>
        <Empty
          customMessage={t('projects:addIn.extensions.proman.noCollections')}
        />
      </div>
    );
  }

  return (
    <>
      <div className={classes.searchBar}>
        <Input
          placeholder={t('projects:addIn.extensions.proman.searchReports')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e)}
          allowClear
          // einfügen wenn prioCL update da ist, da onClear aktuell nicht funktioniert
          // onClear={() => onSearchChange('')}
          width={'100%'}
          size="default"
          full={true}
        />
      </div>
      <div style={{ position: 'relative' }}>
        {loadingAllReports && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              minHeight: '200px',
            }}
          >
            <PrioSpinner size="small" />
          </div>
        )}
        {folderCollapseItems.length === 0 &&
        searchQuery.trim() !== '' &&
        !loadingAllReports ? (
          <div className={classes.emptyState}>
            <Empty
              customMessage={t(
                'projects:addIn.extensions.proman.noSearchResults'
              )}
            />
          </div>
        ) : (
          <PrioCollapse
            items={folderCollapseItems}
            activeKey={activeFolders}
            onChange={onFolderChange}
            className={classes.collapseFolderPanel}
          />
        )}
      </div>
    </>
  );
};
